import { router } from 'expo-router';
import { useState } from 'react';
import { Pressable } from 'react-native';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    EditIcon,
    FullScreenContainer,
    H5Regular,
    Icon,
    Input,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    type SpinwheelConnectRequest,
    useSpinwheelConnect,
} from '../../hooks/useSpinwheelConnect';

const SpinwheelPhoneVerification = () => {
    const { trackViewOnce } = useViewEvents();
    const user = useAuthedUser();
    const { mutate: connectToSpinwheel, isPending: isConnecting } =
        useSpinwheelConnect();

    trackViewOnce(`Onboarding/SpinwheelPhoneVerification`);

    // Start with user's current phone number, but allow editing
    const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '');
    const [isEditing, setIsEditing] = useState(false);

    // Helper function to format phone number for display
    const formatPhoneForDisplay = (phone: string) => {
        if (!phone) return '';
        return phone
            .replace(/\D/g, '')
            .replace(/^1?(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
    };

    // Helper function to convert formatted phone back to E.164
    const formatPhoneForAPI = (phone: string) => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        // If it's 10 digits, add +1, if it's 11 digits starting with 1, add +
        if (digits.length === 10) {
            return `+1${digits}`;
        } else if (digits.length === 11 && digits.startsWith('1')) {
            return `+${digits}`;
        }
        return phone; // Return as-is if it doesn't match expected format
    };

    const handleGoBack = () => {
        router.back();
    };

    const handleEditPhone = () => {
        setIsEditing(true);
    };

    const handleContinue = () => {
        const { dateOfBirth: userDob, id: userId } = user;

        if (userDob == null || !userId || !phoneNumber) {
            console.error('Date of birth, phone number, or user ID is missing');
            return;
        }

        // Format date of birth to YYYY-MM-DD (remove time part)
        const dateOfBirth = new Date(userDob)
            .toISOString()
            .split('T')[0] as string;
        const extUserId = userId;

        const requestBody: SpinwheelConnectRequest = {
            dateOfBirth,
            extUserId,
            phoneNumber: formatPhoneForAPI(phoneNumber),
            isPhoneNumberReset: true, // This request is always from the phone reset page
        };

        connectToSpinwheel(
            {
                body: requestBody,
            },
            {
                onSuccess: (data) => {
                    // Navigate to verification page with spinwheelUserId
                    router.navigate({
                        pathname: '/onboarding/expenses/verification',
                        params: {
                            spinwheelUserId: data.spinwheelUserId,
                        },
                    });
                },
                onError: (error: Error) => {
                    // Extract error details from the json property
                    const errorCode = (
                        error as Error & { json?: { errorCode?: string } }
                    )?.json?.errorCode;

                    // Check for phone format error - stay on this page
                    if (errorCode === 'INVALID_PHONE_FORMAT') {
                        // TODO: Show error message to user
                        console.log(
                            'Phone format still invalid, staying on page',
                        );
                    } else {
                        // TODO: Handle other errors (show toast, etc.)
                        console.error('Other spinwheel error:', error);
                    }
                },
            },
        );
    };

    return (
        <FullScreenContainer
            header={
                <XStack
                    padding={8}
                    flex={1}
                    justifyContent="flex-start"
                    alignItems="center"
                >
                    <BackButton
                        fallbackRoute="/"
                        customBackFn={handleGoBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                </XStack>
            }
        >
            <YStack
                flex={1}
                alignItems="center"
                justifyContent="center"
                gap={32}
            >
                <YStack alignItems="flex-start" gap={16}>
                    <H5Regular textAlign="center" color="$black">
                        We were unable to find your credit report
                    </H5Regular>
                    <BodyMediumRegular color="$gray1000" textAlign="center">
                        Please double-check the phone number below.
                    </BodyMediumRegular>
                </YStack>

                <YStack width="100%" gap={16}>
                    {isEditing ? (
                        <Input
                            label="Phone number"
                            value={formatPhoneForDisplay(phoneNumber)}
                            onChangeText={(text) => {
                                // Store the raw digits, but display formatted
                                const digits = text.replace(/\D/g, '');
                                setPhoneNumber(digits);
                            }}
                            placeholder="(xxx) xxx-xxxx"
                            keyboardType="phone-pad"
                        />
                    ) : (
                        <Pressable onPress={handleEditPhone}>
                            <XStack
                                backgroundColor="$gray200"
                                padding={16}
                                borderRadius={12}
                                borderWidth={1}
                                borderColor="$gray400"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <BodyMediumRegular color="$gray900">
                                    Phone number
                                </BodyMediumRegular>
                                <BodyMediumRegular
                                    color="$black"
                                    fontWeight="emphasized"
                                >
                                    {formatPhoneForDisplay(phoneNumber) ||
                                        '(xxx) xxx-xxxx'}
                                </BodyMediumRegular>
                                <Icon
                                    icon={EditIcon}
                                    size={16}
                                    color="$gray900"
                                />
                            </XStack>
                        </Pressable>
                    )}
                </YStack>

                <Button
                    text="This information is correct"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={handleContinue}
                    disabled={isConnecting || !phoneNumber}
                />
            </YStack>
        </FullScreenContainer>
    );
};

export default SpinwheelPhoneVerification;
